import React, { Fragment, useRef, useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import * as XLSX from 'xlsx';

// MUI Components
import { 
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, 
  Paper, TablePagination, Checkbox, TableFooter, TextField, Autocomplete, 
  IconButton, Box 
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

// MUI Icons
import FirstPageIcon from '@mui/icons-material/FirstPage';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import LastPageIcon from '@mui/icons-material/LastPage';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';

// Custom Components
import DesignationsEditable from './DesignationsEditable';
import DesignationsReadOnly from './DesignationsReadOnly';
import Popup from 'reactjs-popup';
import PopupTable from '../../PopupTable';
import ExcelDownload from '../../../data/exceldownload';

// Redux (RTK Query)
import { 
  useAddNewdesignationMutation, useDeletedesignationMutation, 
  useGetdesignationsQuery, useUpdatedesignationMutation, 
  useAddBulkdesignationMutation 
} from '../../../features/designations/designationApiSlice';

// Styled Components
import { HeadingWrap, Span, CommonButton } from '../../../Styles/CommonStyles';
import { TableContStyle, TableHeadStyle } from '../../../Styles/TableStyles';

// MUI Icons
const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

// Styled Components Definitions
const Addcategform = styled.form`
  display: flex;
  margin: 10px;
  justify-content: space-between;
`;

const FormEleWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
`;

const Displaydiv1 = styled.div``;

const SubmitButton = styled(CommonButton)`
  height: 40px;
  margin-top: 0px;
  margin-bottom: 0px;
`;

const Button2Div = styled.div`
  display: flex;
  justify-content: end;
`;

const BulkWrapper = styled.div`
  display: flex;
  align-items: center;
`;

const InputFile = styled.input`
  width: 180px;
  display: none;
`;

const CustomFileUploadButton = styled.label`
  background-color: #4caf50; /* Green */
  color: white;
  padding: 10px 15px;
  border: none;
  border-radius: 5px;
  cursor: pointer;
`;

const SelectFileReset = styled.button`
  background-color: transparent;
  color: #999;
  border: none;
  font-size: 35px;
  cursor: pointer;
  padding: 0;
`;

// Pagination Component
function TablePaginationActions(props) {
  const theme = useTheme();
  const { count, page, rowsPerPage, onPageChange } = props;

  const handleFirstPageButtonClick = (event) => {
    onPageChange(event, 0);
  };

  const handleBackButtonClick = (event) => {
    onPageChange(event, page - 1);
  };

  const handleNextButtonClick = (event) => {
    onPageChange(event, page + 1);
  };

  const handleLastPageButtonClick = (event) => {
    onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
  };

  return (
    <Box sx={{ flexShrink: 0, ml: 2.5 }}>
      <IconButton
        onClick={handleFirstPageButtonClick}
        disabled={page === 0}
        aria-label="first page"
      >
        {theme.direction === 'rtl' ? <LastPageIcon /> : <FirstPageIcon />}
      </IconButton>
      <IconButton
        onClick={handleBackButtonClick}
        disabled={page === 0}
        aria-label="previous page"
      >
        {theme.direction === 'rtl' ? <KeyboardArrowRight /> : <KeyboardArrowLeft />}
      </IconButton>
      <IconButton
        onClick={handleNextButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="next page"
      >
        {theme.direction === 'rtl' ? <KeyboardArrowLeft /> : <KeyboardArrowRight />}
      </IconButton>
      <IconButton
        onClick={handleLastPageButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="last page"
      >
        {theme.direction === 'rtl' ? <FirstPageIcon /> : <LastPageIcon />}
      </IconButton>
    </Box>
  );
}

// Prop Types
TablePaginationActions.propTypes = {
  count: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  page: PropTypes.number.isRequired,
  rowsPerPage: PropTypes.number.isRequired,
};

// Constants
const tableFontSize = 18;

export default function CustomPaginationActionsTable() {

  // State for Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  // Fetch Designations Data
  const { designations } = useGetdesignationsQuery("desigList", {
    selectFromResult: ({ data }) => ({
      designations: data?.entities,
    }),
  });

  // Mutations
  const [addNewDesignation, { isLoading: isAdding }] = useAddNewdesignationMutation();
  const [addBulkDesignation, { isLoading: isBulkAdding }] = useAddBulkdesignationMutation();
  const [updateDesignation, { isLoading: isUpdating }] = useUpdatedesignationMutation();
  const [
    deleteDesignation
  ] = useDeletedesignationMutation();

  // File Upload & Table Data
  const inputFile = useRef(null);
  const [tabledata, setTabledata] = useState([]);
  const [file, setFile] = useState(null);
  const [fileName, setFileName] = useState("");
  const [fileError, setFileError] = useState(null);
  const [downloadData, setDownloadData] = useState({});

  // Form States
  const [editCategory, setEditCategory] = useState(null);
  const [editFormData, setEditFormData] = useState({
    id: "",
    name: "",
    level: "",
  });
  const [addNewDesgn, setAddNewDesgn] = useState({
    name: "",
    level: "",
  });
  const [searchData, setSearchData] = useState({
    designation: "",
    level: [],
  });

  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - tabledata.length) : 0;

  // Handlers for Pagination
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleButtonClick = () => {
    inputFile.current.click();
  };

  const onSubmit = async (event) => {
    event.preventDefault();
  
    try {
      const bodyObject = { ...addNewDesgn };
  
      // Check if designation already exists
      const isDesignationExists = Object.values(designations).some(
        (el) => el.name === addNewDesgn.name
      );
  
      if (isDesignationExists) {
        alert(`Designation already exists`);
        resetForm();
        return;
      }
  
      // Validate designation name
      if (!addNewDesgn?.name?.length) {
        alert(`Please enter a value for designation`);
        return;
      }
  
      // Validate level
      if (addNewDesgn?.level == null || addNewDesgn.level === '') {
        alert('Please select a value for Level');
        return;
      }
  
      // Add new designation
      await addNewDesignation(bodyObject);
      alert(
        `New Designation ${bodyObject.name} at level ${bodyObject.level} created`
      );
  
      // Reset form after successful submission
      resetForm();
    } catch (err) {
      console.error('Error submitting designation:', err);
      alert('An error occurred while submitting the designation');
    }
  };
  
  // Helper function to reset form data
  const resetForm = () => {
    setAddNewDesgn({
      name: '',
      level: '',
    });
  };
  
  const onClickEdit = (k, row) => {
    setEditFormData(prev => ({
      ...prev,
      id: row.id,
      name: row.name,
      level: row.level
    }));
    setEditCategory(k + 1);
  };

  const onChangeAddDesgn = (event, newValue, name) => {
    setAddNewDesgn(prev => ({
      ...prev,
      ...(event?.target?.name ? { [event.target.name]: event.target.value } : {}),
      ...(name ? { [name]: newValue ? parseInt(newValue) : null } : {})  // Ensure null instead of NaN
    }));
  };
  

  const onChangeEditDesgn = (event, newValue, name) => {
    setEditFormData(prev => ({
      ...prev,
      ...(event.target?.name ? { [event.target.name]: event.target.value } : {}),
      ...(name ? { [name]: newValue ? parseInt(newValue) : null } : {})
    }));
  }; 

  const handleEditFormSubmit = async (e) => {
    e.preventDefault();
  
    if (!editFormData?.name?.trim() || isNaN(Number(editFormData?.level))) {
      return alert("Please select all values before submitting");
    }
    
  
    const isDuplicate = Object.values(designations)?.some(
      (a) => a._id !== editFormData?.id && a.name.trim().toLowerCase() === editFormData?.name.trim().toLowerCase()
    );
  
    if (isDuplicate) {
      return alert("Designation already exists in the database.");
    }
  
    try {
      setEditCategory(null);
      await updateDesignation(editFormData);
      alert(`Designation "${editFormData?.name}" updated successfully`);
    } catch (err) {
      console.error("Error updating designation:", err);
      alert("An error occurred while updating the designation");
    }
  }; 
  

  const handleCancelClick = (e,k) => {    
    setEditCategory(null);
  };

  const handleDeleteClick = async (e, k, row) => {
    e.preventDefault();
    const confirmation = window.confirm(`Do you want to delete designation "${row.name}" at level "${row.level}"?`);
    if (!confirmation) return;

    try {
      const result = await deleteDesignation({ id: row.id });

      if (result?.data?.id) {
        alert(`Designation "${row.name}" deleted successfully`);
        setSearchData(prev => ({
          ...prev,
          designation: "",
          level: []
        }));
      } else {
        alert("Failed to delete designation. Please try again.");
      }
    } catch (error) {
      console.error("Error deleting designation:", error);
      alert("An error occurred while deleting the designation.");
    }
  };
  

  const deleteSelectFile = () => {
    if (inputFile.current) {
      inputFile.current.value = "";
    }
    setFile(null);
  };
  

  const onBulkSubmit = async (e) =>{
    e.preventDefault();

    if (!file || file.length === 0) {
      return alert("Please select a file.");
    }

    const reducedArray = file.map(val => ({
      name: val.name,
      level: val.level
    }));

    const existingNames = new Set(Object.values(designations).map(item => item.name));
    const filteredArray = reducedArray.filter(item => !existingNames.has(item.name));
    const duplicateArray = reducedArray.filter(item => existingNames.has(item.name))

    if (filteredArray.length === 0) {
      return alert("All designations already exist in the database.");
    }

    try {
      const response = await addBulkDesignation(filteredArray);
      alert(`${response?.data?.insertedCount} designations inserted. ${duplicateArray?.length? `${duplicateArray?.length} duplicate values found.`:""}`)
      console.log(response);
      inputFile.current.value = "";
      setFile(null);
      setFileName("");

    } catch (error) {
      console.error("Error adding bulk designations:", error);
      alert("An error occurred while adding designations.");
    }
  }

  const fileType = [
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    "application/vnd.ms-excel"
  ];
  
  const handleChangeDesig = (e) => {
    let selectedFile = e.target.files[0];
  
    if (!selectedFile) {
      setFileError("No file selected");
      setFile(null);
      return;
    }
  
    if (!fileType.includes(selectedFile.type)) {
      setFileError("Please select a valid Excel file (.xlsx or .xls)");
      setFile(null);
      inputFile.current.value = "";
      return;
    }
  
    setFileName(selectedFile.name);
    setFileError(null);
  
    let reader = new FileReader();
    reader.readAsArrayBuffer(selectedFile);
  
    reader.onload = (e) => {
      try {
        const workbook = XLSX.read(e.target.result, { type: "buffer" });
        const worksheet = workbook.Sheets[workbook.SheetNames[0]];
        const data = XLSX.utils.sheet_to_json(worksheet);
        setFile(data);
      } catch (error) {
        console.error("Error reading file:", error);
        setFileError("Error processing file");
        setFile(null);
      }
    };
  
    reader.onerror = () => {
      console.error("File reading error");
      setFileError("Failed to read file");
      setFile(null);
    };
  };
  

  const handleSearchChange = (event, newValue, name) =>{
    let tempSearchData = { ...searchData };

    if (event?.target?.name) {
      tempSearchData[event.target.name] = event.target.value;
    } else {
      tempSearchData[name] = newValue;
    }

    setSearchData(tempSearchData);

    let filteredData = Object.values(designations).filter(item => {
      return Object.keys(tempSearchData).every(key => {
        const searchValue = tempSearchData[key];
  
        if (!searchValue || searchValue.length === 0) return true;
  
        if (typeof searchValue === "string") {
          return item[key]?.toLowerCase().includes(searchValue.toLowerCase());
        }
  
        if (Array.isArray(searchValue)) {
          return searchValue.includes(item[key]);
        }
  
        return true;
      });
    });
  
    setTabledata(filteredData.sort((a, b) => b.level - a.level));
     
  }

  const uniqueSortedLevels = useMemo(() => {
    if (!designations) return [];
  
    return [...new Set(Object.values(designations).map(x => x.level))].sort((a, b) => a - b);
  }, [designations]);

  const paginatedData = useMemo(() => 
    rowsPerPage > 0 ? tabledata.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage) : tabledata
  , [tabledata, page, rowsPerPage]);

  useEffect(() => {
    if (!designations) return;
  
    const sortedArray = Object.values(designations).toSorted((a, b) => b.level - a.level);
  
    setTabledata(sortedArray);  

  }, [designations]);

  //Setting the download data for excel download.
  useEffect(() => {
    const keyOrderArray = ["name", "level"];

    const finalSortedDownloadData = tabledata.map(({ _id, ...rest }) => {  //Removes _id from the data
      let tempObject = {};
      keyOrderArray.forEach(key => {
        tempObject[key] = rest[key];
      });
      return tempObject;
    });

    setDownloadData(finalSortedDownloadData);
  }, [tabledata]);

  return (
    <>
    <Displaydiv1>
      <HeadingWrap>
        <Span>Add New Designation</Span>
      </HeadingWrap>

      <Addcategform onSubmit={onSubmit}>
        <FormEleWrapper>
          <TextField
            id="outlined-basic"
            size="small"
            name="name"
            onChange={onChangeAddDesgn}
            label="Enter a Designation"
            value={addNewDesgn?.name || ""}
            variant="outlined"
            sx={{ backgroundColor: "white", width: "200px", height: "40px" }}
          />

          <Autocomplete
            disablePortal
            id="combo-box-demo"
            size="small"
            isOptionEqualToValue={(option, value) => option === value}
            value={addNewDesgn?.level ? String(addNewDesgn.level) : ""}
            onChange={(event, newValue) =>
              onChangeAddDesgn(event, newValue ? parseInt(newValue, 10) : null, "level")
            }
            options={Array.from({ length: 20 }, (_, index) => String(index + 1))}
            sx={{ backgroundColor: "white", width: "200px", marginLeft: "20px", marginRight: "20px", height: "40px" }}
            renderInput={(params) => <TextField {...params} label="Select Level" />}
          />

          <SubmitButton type="submit" disabled = {isAdding || isBulkAdding} >{isAdding? "Loading":"Submit"}</SubmitButton>
        </FormEleWrapper>

        <Button2Div>
          <ExcelDownload data={downloadData ?? []} filename="designations" sheetName="sheet1" />
        </Button2Div>
        
      </Addcategform>
    

    <form onSubmit={handleEditFormSubmit}>

    <TableContainer component={Paper} sx={{ backgroundColor: TableContStyle.backgroundColor }}>
      <Table  aria-label="custom pagination table">
      <TableHead>
        <TableRow sx={{ backgroundColor: TableHeadStyle.backgroundColor}}>
          <TableCell style={{ fontSize: TableHeadStyle.fontSize, fontWeight: TableHeadStyle.fontWeight }} sx={{width:{md:"5%"}}}align="left" >Sl.No.</TableCell>
          <TableCell style={{ fontSize: TableHeadStyle.fontSize, fontWeight: TableHeadStyle.fontWeight }} sx={{width:{md:"35%"}}} align="left">Designation</TableCell>
          <TableCell style={{ fontSize: TableHeadStyle.fontSize, fontWeight: TableHeadStyle.fontWeight }} sx={{width:{md:"30%"}}} align="left">Level</TableCell>       
          <TableCell style={{ fontSize: TableHeadStyle.fontSize, fontWeight: TableHeadStyle.fontWeight }} sx={{width:{md:"30%"}}} align="left">Edit/Delete</TableCell>
        </TableRow>

        <TableRow sx={{ backgroundColor: TableHeadStyle.backgroundColor}}>
          <TableCell >
          </TableCell>
          <TableCell >
            <TextField sx={{background:"white"}}  name='name' onChange={handleSearchChange} label="Search Designation" size="small"  variant="outlined" />
          </TableCell>
          <TableCell >
            <Autocomplete
              multiple
              id="checkboxes-tags-demo"
              onChange={(event, newValue) => handleSearchChange(event, newValue, "level")}
              size="small"
              value={searchData.level}
              options={uniqueSortedLevels}
              disableCloseOnSelect
              getOptionLabel={(option) => String(option)}
              renderOption={(props, option, { selected }) => (
                <li {...props} style={{ fontSize: '2 rem' }}>
                  <Checkbox
                    icon={icon}
                    checkedIcon={checkedIcon}
                    style={{ marginRight: 0, marginLeft: 0 }}
                    checked={selected}
                  />
                  {option}
                </li>
              )}
              renderInput={(params) => (
                <TextField {...params} label="Search Level" sx={{ background: "white", width: "50%" }} />
              )}
            />           
          </TableCell>
          <TableCell >
          </TableCell>
        </TableRow>
      </TableHead>

      <TableBody>
        {paginatedData.map((row, index) => ( 
          <Fragment key={row.id || index}>
            {editCategory === index + 1 ? (              
               <DesignationsEditable
               row={row} k={index} editFormData={editFormData} SubmitButton={SubmitButton} handleCancelClick={handleCancelClick} 
               page={page} rowsPerPage={rowsPerPage} editCategory={editCategory} onChangeEditDesgn={onChangeEditDesgn} 
               tableFontSize={tableFontSize} isUpdating={isUpdating} 
             />
           ) : (
             <DesignationsReadOnly
               row={row} k={index} tabledata={tabledata} onClickEdit={onClickEdit} handleDeleteClick={handleDeleteClick} 
               page={page} rowsPerPage={rowsPerPage} SubmitButton={SubmitButton} tableFontSize={tableFontSize} 
             />
            )}
          </Fragment>                   
        ))}
          {emptyRows > 0 && (
            <TableRow style={{ height: 53 * emptyRows }}>
              <TableCell colSpan={6} />
            </TableRow>
          )}
        </TableBody>
        <TableFooter>
          <TableRow>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, { label: 'All', value: -1 }]}
              colSpan={3}
              count={tabledata.length}
              rowsPerPage={rowsPerPage}
              page={page}
              SelectProps={{
                inputProps: {
                  'aria-label': 'rows per page',
                },
                native: true,
              }}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              ActionsComponent={TablePaginationActions}
            />
          </TableRow>
        </TableFooter>
      </Table>
    </TableContainer>
    </form>

    <form onSubmit={onBulkSubmit}>
      <HeadingWrap>
        <Span>Upload Bulk Designation</Span>
      </HeadingWrap>
     
      <BulkWrapper>
        <InputFile type="file" onChange={handleChangeDesig} ref={inputFile} />

        <CustomFileUploadButton htmlFor="file-upload" onClick={handleButtonClick}>
          Upload File
        </CustomFileUploadButton>

        {file && <><span>{fileName}</span><SelectFileReset type='button' onClick={() => deleteSelectFile()} >&times;</SelectFileReset>
        <Popup trigger={<SubmitButton type='button'>View File</SubmitButton>} position="right center" modal nested>
                <PopupTable props={file}/>
        </Popup></>}

        <SubmitButton type="submit" disabled={isAdding || isBulkAdding}>
          {isBulkAdding ? "Loading..." : "Submit"}
        </SubmitButton>

        {fileError && <span>{fileError}</span>}

      </BulkWrapper>

    </form>    
  </Displaydiv1>
  </>
  );
}